import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FinanceWorkspaceHub } from '@/modules/finance/shared/FinanceWorkspaceHub';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';

export function FinanceReceivablesWorkspacePage() {
  const [openAr, setOpenAr] = useState<string>('—');

  useEffect(() => {
    financeApi.dashboardSummary().then((s) => {
      setOpenAr(s.openReceivables?.toFixed(2) ?? '—');
    }).catch((e) => showApiError(e, 'Failed to load receivables'));
  }, []);

  return (
    <FinanceWorkspaceHub
      title="Receivables"
      description="Bill customers, collect payments, and manage collections — no accounting jargon required"
      breadcrumbs={[{ label: 'Receivables' }]}
      guidance="Create an invoice, send it to your customer, and record payments when they pay. We handle the accounting automatically."
      kpis={[{ title: 'Open receivables', value: openAr }]}
      actions={[
        { to: '/finance/invoices', label: 'Invoices', description: 'Create, preview, and email customer invoices' },
        { to: '/finance/payments', label: 'Customer payments', description: 'Record money received from customers' },
        { to: '/finance/collections', label: 'Collections center', description: 'Overdue AR, dunning, and promise-to-pay' },
        { to: '/finance/reports', label: 'AR aging report', description: 'See who owes you and for how long' },
      ]}
    />
  );
}

export function FinanceTaxWorkspacePage() {
  return (
    <FinanceWorkspaceHub
      title="Tax"
      description="Configure tax codes and calculate tax on transactions"
      breadcrumbs={[{ label: 'Tax' }]}
      actions={[
        { to: '/finance/taxes', label: 'Tax codes', description: 'Sales tax, VAT, GST configuration' },
        { to: '/finance/compliance', label: 'Global compliance', description: 'Country tax packs and validation' },
        { to: '/finance/setup/wizard', label: 'Setup wizard', description: 'Guided tax setup during onboarding' },
        { to: '/finance/reports', label: 'Tax reports', description: 'Review tax-related financial reports' },
      ]}
    />
  );
}

export function FinancePayablesWorkspacePage() {
  return (
    <FinanceWorkspaceHub
      title="Payables"
      description="Pay vendors, manage expenses, and run payment batches — we post to the ledger for you"
      breadcrumbs={[{ label: 'Payables' }]}
      guidance="Record vendor bills, approve expenses, and schedule payment runs. Accounting entries are created automatically when you post."
      actions={[
        { to: '/finance/payables/ap', label: 'AP & payment runs', description: 'Vendor bills, payment batches, and 3-way match' },
        { to: '/finance/documents', label: 'Document capture', description: 'OCR vendor bills and receipts before posting' },
        { to: '/finance/expenses', label: 'Employee expenses', description: 'Submit, approve, and reimburse expenses' },
        { to: '/vendor-portal/login', label: 'Vendor portal', description: 'Supplier self-service for POs, bills, and payments' },
      ]}
    />
  );
}

export function FinanceAdministrationWorkspacePage() {
  return (
    <FinanceWorkspaceHub
      title="Administration"
      description="Setup, close, health, workflows, and platform governance"
      breadcrumbs={[{ label: 'Administration' }]}
      headerActions={
        <Link to="/finance/setup/wizard" className="btn btn-primary btn-sm">
          Setup wizard
        </Link>
      }
      actions={[
        { to: '/finance/setup/wizard', label: 'Setup wizard', description: 'First-time guided finance configuration' },
        { to: '/finance/health', label: 'Finance health center', description: 'Issues, warnings, and one-click fixes' },
        { to: '/finance/close', label: 'Period close', description: 'Month, quarter, and year-end close checklist' },
        { to: '/finance/automation', label: 'Automation center', description: 'Scheduled jobs and manual runs' },
        { to: '/finance/integrations', label: 'Integration health', description: 'Cross-module accounting event status' },
        { to: '/finance/audit', label: 'Audit trail', description: 'Who changed what, when, and why' },
        { to: '/finance/setup', label: 'Finance setup', description: 'Chart of accounts, fiscal periods, budgets' },
        { to: '/finance/workflows/designer', label: 'Approval workflows', description: 'Configure who approves what' },
        { to: '/finance/control/rules', label: 'Business rules', description: 'No-code IF/THEN approval rules' },
        { to: '/finance/administration', label: 'Users & subscription', description: 'Module administration (platform)' },
      ]}
    />
  );
}
